import type { ApiClient } from '@carbonwebmail/api-client'
import type { TicketListDto, TicketThreadDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import { useAction } from '../../ui/useAction.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Asking for help, and following the answer.
 *
 * The open thread is held here rather than in the route, because opening one
 * is not navigation: somebody reads a reply, answers it, and goes back to the
 * list, and putting that in the url would add history entries for a panel
 * sliding in and out.
 *
 * Every write reloads both the list and the open thread. A reply changes the
 * thread's status and its position in the list, and a screen that showed the
 * new message beside a stale status would be telling two stories.
 */
export function useSupport({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.tickets(), [client])
  const { state, reload } = useLoaded<TicketListDto>(
    load,
    'We could not load your tickets. Please try again in a moment.',
  )

  const [thread, setThread] = useState<TicketThreadDto>()
  const { notice, problem, busy, run } = useAction(reload)

  const openThread = useCallback(
    async (ticketId: string) => {
      setThread(await client.ticket(ticketId))
    },
    [client],
  )

  const closeThread = useCallback(() => setThread(undefined), [])

  /** After a write: the thread and the list both moved, so both are re-read. */
  const refresh = useCallback(
    async (ticketId: string) => {
      setThread(await client.ticket(ticketId))
    },
    [client],
  )

  const open = useCallback(
    (subject: string, body: string) =>
      run(async () => {
        const { id } = await client.openTicket({ subject, body })
        await refresh(id)
        return 'Sent. We will reply here.'
      }, 'We could not open that ticket. Please try again.'),
    [client, run, refresh],
  )

  const reply = useCallback(
    (ticketId: string, body: string) =>
      run(async () => {
        await client.replyToTicket(ticketId, body)
        await refresh(ticketId)
        return 'Sent.'
      }, 'We could not send that reply. Please try again.'),
    [client, run, refresh],
  )

  const close = useCallback(
    (ticketId: string) =>
      run(async () => {
        await client.closeTicket(ticketId)
        await refresh(ticketId)
        return 'Closed. Replying will reopen it.'
      }, 'We could not close that ticket. Please try again.'),
    [client, run, refresh],
  )

  return {
    state,
    thread,
    notice,
    problem,
    busy,
    openThread,
    closeThread,
    open,
    reply,
    close,
    retry: reload,
  }
}
